import type { ExtendedTier, Role, RateMultiplier } from '@/types';
import {
  getThreshold,
  getSampleRole,
  getStraightTimeMultiplier,
  getSortedOvertimeMultipliers,
  getMultiplierListStringHorizontal,
} from '@/utils/rateCalculations';

export function calculateHolidayMarkdownVariables(
  roles: Role[] = [],
  rateMultipliers: RateMultiplier[] = [],
  holidayHourlyPremiumType?: 'percentage' | 'fixed' | null,
  holidayHourlyPremiumPercentageValue?: number | null,
  holidayHourlyPremiumFixedValue?: number | null,
  holidayPremiumBasisIsOtInclusive?: boolean,
  holidayOffsetAmount: number = 1,
  holidayMultiplierFloorCode?: string | null,
  holidayFixedPremiumAmount?: number | null,
  holidayExtendedOvertimeEnabled?: boolean,
  holidayExtendedOvertimeTiers: ExtendedTier[] = [],
  holidayMultiplierFloorCustomValue?: number | null
) {
  const sampleRole = getSampleRole(roles);
  const example_role_rate = sampleRole.rate;
  const example_role_unit = sampleRole.billingStructure === 'hourly' ? '/hr' : '/day';
  const billingStructure = sampleRole.billingStructure;
  const isDayRate = billingStructure === 'day_rate';

  const STRAIGHT_TIME = getStraightTimeMultiplier(rateMultipliers);
  const sortedOvertimeMultipliers = getSortedOvertimeMultipliers(rateMultipliers, billingStructure);
  
  // A multiplier with a 0 threshold acts as the base rate, replacing STRAIGHT_TIME.
  const baseMultiplier = sortedOvertimeMultipliers.find(m => (getThreshold(m, billingStructure) ?? 0) === 0) || STRAIGHT_TIME;
  const overtimeLadder = sortedOvertimeMultipliers.filter(m => (getThreshold(m, billingStructure) ?? 0) > 0);
  const fullLadder = [baseMultiplier, ...overtimeLadder];
  const highestBaseMultiplier = fullLadder[fullLadder.length - 1]?.defaultMultiplier ?? 1;
  const holidayExtendedTiers = holidayExtendedOvertimeEnabled
    ? [...holidayExtendedOvertimeTiers]
        .filter(tier => Number.isFinite(Number(tier.rate)) && Number(tier.rate) > highestBaseMultiplier)
        .sort((a, b) => a.sortOrder - b.sortOrder)
    : [];

  const resolvedOffsetAmount = Math.max(1, Math.round(holidayOffsetAmount || 1));

  const getOffsetTarget = (index: number): {
    name: string;
    defaultMultiplier: number | null;
    topMode: 'base_ladder' | 'extended_tier' | 'capped';
  } => {
    const targetIndex = index + resolvedOffsetAmount;

    // Target lands within the base ladder.
    if (targetIndex < fullLadder.length) {
      const target = fullLadder[targetIndex];
      return {
        name: target.name,
        defaultMultiplier: target.defaultMultiplier,
        topMode: 'base_ladder',
      };
    }

    // Target overshoots the base ladder — step into extended tiers.
    const extendedIndex = targetIndex - fullLadder.length;
    const extendedTier = holidayExtendedTiers[Math.min(extendedIndex, Math.max(holidayExtendedTiers.length - 1, 0))];
    if (extendedTier) {
      return {
        name: extendedTier.name || 'Extended holiday tier',
        defaultMultiplier: Number(extendedTier.rate),
        topMode: 'extended_tier',
      };
    }

    // At the top of the ladder with no extended tiers — cap here.
    // Enable extended tiers to define rates above the ladder.
    const capEntry = fullLadder[fullLadder.length - 1];
    return {
      name: capEntry?.name ?? 'Top tier',
      defaultMultiplier: capEntry?.defaultMultiplier ?? 1,
      topMode: 'capped',
    };
  };

// Determine example shift duration to showcase the full ladder
  const highestThreshold = Math.max(0, ...overtimeLadder.map(m => getThreshold(m, billingStructure) ?? 0));
  
  // Check if the primary ladder for this billing structure is daily or weekly
  const isDailyLadder = overtimeLadder.length > 0 && overtimeLadder[0].defaultPerShiftThresholdHours != null;
  
  let example_shift_duration;
  if (isDailyLadder) {
    // If it's a daily ladder (per-shift), create an example long enough to show the ladder.
    example_shift_duration = highestThreshold + 2;
  } else {
    // If it's a weekly ladder, use a realistic single day's work for the example.
    const firstOtThreshold = getThreshold(overtimeLadder[0], 'hourly');
    example_shift_duration = firstOtThreshold ? Math.min(firstOtThreshold / 5 + 2, 12) : 10; // e.g. 40hr/week -> 10hr day
  }

  // Derive an hourly rate for calculations
  const firstOtThreshold = getThreshold(overtimeLadder[0], billingStructure) ?? (isDayRate ? 8 : 40);
  const hourlyRate = isDayRate ? example_role_rate / firstOtThreshold : example_role_rate;

  const rateMultiplierLadder = getMultiplierListStringHorizontal(rateMultipliers, billingStructure, isDayRate ? firstOtThreshold : null);
  const proratedRateDisplay = isDayRate ? ` ($${hourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr prorated)` : '';

  // Resolve defaults so tables use fallback values whenever inputs are absent
  const resolvedHolidayUpliftType: 'percentage' | 'fixed' =
    (holidayHourlyPremiumType === 'percentage' || holidayHourlyPremiumType === 'fixed') ? holidayHourlyPremiumType : 'fixed';
  const resolvedHolidayUpliftPercentage = Number(holidayHourlyPremiumPercentageValue) || 10;
  const resolvedHolidayUpliftAmount = Number(holidayHourlyPremiumFixedValue) || 15;

  // --- Normal Day Calculation ---
  const normalCalculationRows = [];
  let normal_day_total_pay = 0;
  let hoursAccountedFor = 0;

  for (let i = 0; i < fullLadder.length; i++) {
    const currentMultiplier = fullLadder[i];
    const currentThreshold = getThreshold(currentMultiplier, billingStructure) ?? 0;
    const nextThreshold = (i + 1 < fullLadder.length) ? getThreshold(fullLadder[i+1], billingStructure) ?? Infinity : Infinity;
    
    if (hoursAccountedFor >= example_shift_duration) break;

    const hoursInBracket = Math.min(example_shift_duration - hoursAccountedFor, nextThreshold - currentThreshold);
    if (hoursInBracket <= 0) continue;

    const payRate = hourlyRate * currentMultiplier.defaultMultiplier!;
    const grossPay = hoursInBracket * payRate;
    normal_day_total_pay += grossPay;
    
    let hours_worked;
    if (i === 0) {
      hours_worked = `First ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    } else if (nextThreshold === Infinity) {
      hours_worked = `Final ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    } else {
      hours_worked = `Next ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    }
    
    normalCalculationRows.push({
      hours_worked,
      rate_name: currentMultiplier.name,
      multiplier: `${currentMultiplier.defaultMultiplier!}x`,
      calculation: `${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hrs × $${payRate.toLocaleString(undefined, { minimumFractionDigits: payRate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}/hr`,
      gross_pay: `$${grossPay.toLocaleString(undefined, { minimumFractionDigits: grossPay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`
    });

    hoursAccountedFor += hoursInBracket;
  }

  // Join normal calculation steps with plus signs for display in markdown
  const normal_calculation_formula = normalCalculationRows
    .map((row: { calculation: string }, idx: number) => `${idx ? ' + ' : ''}(${row.calculation})`)
    .join('');

  // --- Holiday Calculation (with Offset) ---
  const holidayCalculationRows = [];
  let holiday_day_total_pay = 0;
  hoursAccountedFor = 0;

  for (let i = 0; i < fullLadder.length; i++) {
    const currentMultiplier = fullLadder[i];
    const currentThreshold = getThreshold(currentMultiplier, billingStructure) ?? 0;
    const nextThreshold = (i + 1 < fullLadder.length) ? getThreshold(fullLadder[i+1], billingStructure) ?? Infinity : Infinity;

    if (hoursAccountedFor >= example_shift_duration) break;

    const hoursInBracket = Math.min(example_shift_duration - hoursAccountedFor, nextThreshold - currentThreshold);
    if (hoursInBracket <= 0) continue;

    const offsetMultiplier = getOffsetTarget(i);

    const payRate = hourlyRate * offsetMultiplier.defaultMultiplier!;
    const grossPay = hoursInBracket * payRate;
    holiday_day_total_pay += grossPay;
    
    let hours_worked;
    if (i === 0) {
      hours_worked = `First ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    } else if (nextThreshold === Infinity) {
      hours_worked = `Final ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    } else {
      hours_worked = `Next ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    }

    const normalPayRate = hourlyRate * currentMultiplier.defaultMultiplier!;
    const normalGrossPay = hoursInBracket * normalPayRate;
    const holidayPremium = grossPay - normalGrossPay;

    holidayCalculationRows.push({
      hours_worked,
      original_rate_name: currentMultiplier.name,
      original_multiplier: `${currentMultiplier.defaultMultiplier!}x`,
      holiday_rate_name: offsetMultiplier.name,
      holiday_multiplier: `${offsetMultiplier.defaultMultiplier!}x`,
      gross_pay_before_surcharge: `$${normalGrossPay.toLocaleString(undefined, { minimumFractionDigits: normalGrossPay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`,
      gross_pay_before_surcharge_calculation: `${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hrs × $${hourlyRate.toLocaleString(undefined, { minimumFractionDigits: hourlyRate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}/hr × ${currentMultiplier.defaultMultiplier!}x`,
      gross_pay_after_surcharge: `$${grossPay.toLocaleString(undefined, { minimumFractionDigits: grossPay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`,
      gross_pay_after_surcharge_calculation: `${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hrs × $${hourlyRate.toLocaleString(undefined, { minimumFractionDigits: hourlyRate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}/hr × ${offsetMultiplier.defaultMultiplier!}x`,
      holiday_premium: `$${holidayPremium.toLocaleString(undefined, { minimumFractionDigits: holidayPremium % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`,
      gross_pay: `$${grossPay.toLocaleString(undefined, { minimumFractionDigits: grossPay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`
    });

    hoursAccountedFor += hoursInBracket;
  }
  
  const holiday_premium = holiday_day_total_pay - normal_day_total_pay;

  // --- Holiday Calculation (with Multiplier Floor) ---
  const fixedComparisonRows = [];
  let floor_holiday_day_total_pay = 0;
  hoursAccountedFor = 0;

  const configuredFloorMultiplier = holidayMultiplierFloorCode
    ? rateMultipliers.find(m => m.code === holidayMultiplierFloorCode)
    : null;
  const customFloorMultiplierValue = Number.isFinite(holidayMultiplierFloorCustomValue)
    ? Number(holidayMultiplierFloorCustomValue)
    : null;
  const configuredCustomFloorMultiplier: RateMultiplier | null = customFloorMultiplierValue != null
    ? {
        code: 'CUSTOM_RATE',
        name: 'Custom rate',
        defaultMultiplier: customFloorMultiplierValue,
        perShiftBillingStructures: null,
        perWeekBillingStructures: null,
      }
    : null;
  const exampleFloorMultiplier = configuredFloorMultiplier || configuredCustomFloorMultiplier || overtimeLadder[0] || baseMultiplier;
  
  // Add after exampleFloorMultiplier is defined
  const floorHolidayCalculationRows: Array<{
    hours_worked: string;
    natural_rate: string;
    holiday_floor_rate: string;
    paid_as: string;
    calculation: string;
    gross_pay_before_premium: string;
    gross_pay_before_premium_calculation: string;
    holiday_premium: string;
    gross_pay: string;
  }> = [];
  
  // Add after exampleFloorMultiplier is defined (or near configuredFloorMultiplier)
  const hasAnyNonStraightTimeMultipliers = rateMultipliers.some(m => m.code !== 'STRAIGHT_TIME');
  
  for (let i = 0; i < fullLadder.length; i++) {
    const naturalMultiplier = fullLadder[i];
    const currentThreshold = getThreshold(naturalMultiplier, billingStructure) ?? 0;
    const nextThreshold = (i + 1 < fullLadder.length) ? getThreshold(fullLadder[i+1], billingStructure) ?? Infinity : Infinity;

    if (hoursAccountedFor >= example_shift_duration) break;

    const hoursInBracket = Math.min(example_shift_duration - hoursAccountedFor, nextThreshold - currentThreshold);
    if (hoursInBracket <= 0) continue;

    // For multiplier floor, the effective multiplier is the higher of natural rate or floor rate
    let effectiveMultiplier = naturalMultiplier;
    if (exampleFloorMultiplier.defaultMultiplier! > naturalMultiplier.defaultMultiplier!) {
      effectiveMultiplier = exampleFloorMultiplier;
    }

    const payRate = hourlyRate * effectiveMultiplier.defaultMultiplier!;
    const grossPay = hoursInBracket * payRate;
    floor_holiday_day_total_pay += grossPay;
    
    let hours_worked_label;
    if (i === 0) {
      hours_worked_label = `First ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    } else if (nextThreshold === Infinity) {
      hours_worked_label = `Final ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    } else {
      hours_worked_label = `Next ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    }

    let explanation;
    if (effectiveMultiplier.code === naturalMultiplier.code && effectiveMultiplier.code === exampleFloorMultiplier.code) {
      explanation = `The natural rate and holiday floor are the same.`;
    } else if (effectiveMultiplier.code === naturalMultiplier.code) {
      explanation = `The natural rate (**${naturalMultiplier.name}**) is higher, so it's used.`;
    } else {
      explanation = `The holiday floor (**${exampleFloorMultiplier.name}**) is higher, so it's used.`;
    }

    fixedComparisonRows.push({
        hour_bracket: hours_worked_label,
        normal_day_rate: `**${naturalMultiplier.name}** (${naturalMultiplier.defaultMultiplier!}x)`,
        holiday_rate: `**${effectiveMultiplier.name}** (${effectiveMultiplier.defaultMultiplier!}x)`,
        explanation
    });

    // Add inside the Rate Floor loop, after fixedComparisonRows.push(...)
    const normalPayRate = hourlyRate * naturalMultiplier.defaultMultiplier!;
    const normalGrossPay = hoursInBracket * normalPayRate;
    const holidayPremium = grossPay - normalGrossPay;

    floorHolidayCalculationRows.push({
      hours_worked: hours_worked_label,
      natural_rate: `${naturalMultiplier.name} (${naturalMultiplier.defaultMultiplier!}x)`,
      holiday_floor_rate: `${exampleFloorMultiplier.name} (${exampleFloorMultiplier.defaultMultiplier!}x)`,
      paid_as: `${effectiveMultiplier.name} (${effectiveMultiplier.defaultMultiplier!}x)`,
      calculation: `${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hrs × $${hourlyRate.toLocaleString(undefined, { minimumFractionDigits: hourlyRate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}/hr × ${effectiveMultiplier.defaultMultiplier!}x`,
      gross_pay_before_premium: `$${normalGrossPay.toLocaleString(undefined, { minimumFractionDigits: normalGrossPay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`,
      gross_pay_before_premium_calculation: `${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hrs × $${hourlyRate.toLocaleString(undefined, { minimumFractionDigits: hourlyRate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}/hr × ${naturalMultiplier.defaultMultiplier!}x`,
      holiday_premium: `$${holidayPremium.toLocaleString(undefined, { minimumFractionDigits: holidayPremium % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`,
      gross_pay: `$${grossPay.toLocaleString(undefined, { minimumFractionDigits: grossPay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`,
    });

    hoursAccountedFor += hoursInBracket;
  }
  const floor_holiday_premium = floor_holiday_day_total_pay - normal_day_total_pay;

  // --- Holiday Calculation (with Hourly Premium) ---
  const hourlyPremiumCalculationRows = [];
  let holiday_hourly_premium_total_pay = 0;
  hoursAccountedFor = 0;

  for (let i = 0; i < fullLadder.length; i++) {
    const currentMultiplier = fullLadder[i];
    const currentThreshold = getThreshold(currentMultiplier, billingStructure) ?? 0;
    const nextThreshold = (i + 1 < fullLadder.length) ? getThreshold(fullLadder[i+1], billingStructure) ?? Infinity : Infinity;

    if (hoursAccountedFor >= example_shift_duration) break;

    const hoursInBracket = Math.min(example_shift_duration - hoursAccountedFor, nextThreshold - currentThreshold);
    if (hoursInBracket <= 0) continue;

    const baseGross = hourlyRate * currentMultiplier.defaultMultiplier! * hoursInBracket;
    let premium_amount = 0;
    let premium_rate_per_hour = 0;
    let total_hourly_rate = hourlyRate * currentMultiplier.defaultMultiplier!;
    let gross_pay = baseGross;

    if (holidayPremiumBasisIsOtInclusive) {
      if (resolvedHolidayUpliftType === 'percentage') {
        // Premium basis: prevailing rate
        premium_rate_per_hour = (hourlyRate * currentMultiplier.defaultMultiplier!) * (resolvedHolidayUpliftPercentage / 100);
        premium_amount = premium_rate_per_hour * hoursInBracket;
      } else {
        // Treat missing type as fixed and use $15/hr fallback
        premium_rate_per_hour = resolvedHolidayUpliftAmount;
        premium_amount = premium_rate_per_hour * hoursInBracket;
      }
    } else {
      if (resolvedHolidayUpliftType === 'percentage') {
        // Premium basis: base hourly rate (consistent per hour)
        premium_rate_per_hour = hourlyRate * (resolvedHolidayUpliftPercentage / 100);
        premium_amount = premium_rate_per_hour * hoursInBracket;
      } else {
        // Treat missing type as fixed and use $15/hr fallback
        premium_rate_per_hour = resolvedHolidayUpliftAmount;
        premium_amount = premium_rate_per_hour * hoursInBracket;
      }
    }

    gross_pay += premium_amount;
    holiday_hourly_premium_total_pay += gross_pay;
    total_hourly_rate += premium_rate_per_hour;

    const hoursStr = hoursInBracket.toLocaleString(undefined, { maximumFractionDigits: 2 });
    const prevRateStr = (hourlyRate * currentMultiplier.defaultMultiplier!).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const baseRateStr = hourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const premiumRateStr = premium_rate_per_hour.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    // Detailed premium/hr explanation
    let hourlyPremiumRateStr = `$${premiumRateStr}/hr`;
    if (resolvedHolidayUpliftType === 'percentage') {
      const pct = resolvedHolidayUpliftPercentage;
      if (holidayPremiumBasisIsOtInclusive) {
        hourlyPremiumRateStr += `<br/>($${baseRateStr}/hr × ${currentMultiplier.defaultMultiplier!}x × ${pct}%)`;
      } else {
        hourlyPremiumRateStr += `<br/>($${baseRateStr}/hr × ${pct}%)`;
      }
    } else if (holidayPremiumBasisIsOtInclusive) {
      // fixed + compounding: show multiplier component
      hourlyPremiumRateStr += ``;
    }

    // Premium amount with calculation
    const premiumAmountWithCalc = `${premium_amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}<br/>($${premiumRateStr}/hr × ${hoursStr}h)`;

    // Final gross calculation line - effective hourly rate × hours
    const totalHourlyRateStr = total_hourly_rate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const finalGrossCalculation = `$${totalHourlyRateStr}/hr × ${hoursStr}h`;

    // Effective hourly rate calculation
    const baseHourlyRateStr = (hourlyRate * currentMultiplier.defaultMultiplier!).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const effectiveHourlyRateCalculation = `$${baseRateStr}/hr × ${currentMultiplier.defaultMultiplier!}x + $${premiumRateStr}/hr`;

    let hours_worked_label;
    if (i === 0) {
      hours_worked_label = `First ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    } else if (nextThreshold === Infinity) {
      hours_worked_label = `Final ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    } else {
      hours_worked_label = `Next ${hoursInBracket.toFixed(2).replace(/\.00$/, '')} hours`;
    }

    hourlyPremiumCalculationRows.push({
      hours_worked: hours_worked_label,
      rate_name: currentMultiplier.name,
      multiplier: `${currentMultiplier.defaultMultiplier!}x`,
      // Keep for reference (not used in the new table row)
      uplift_per_hour: premiumRateStr,
      total_hourly_rate: total_hourly_rate.toLocaleString(undefined, { minimumFractionDigits: total_hourly_rate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
      // Values with calculations appended
      gross_pay: `$${gross_pay.toLocaleString(undefined, { minimumFractionDigits: gross_pay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`,
      base_gross: `$${baseGross.toLocaleString(undefined, { minimumFractionDigits: baseGross % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}<br/>(${hoursStr}h × $${holidayPremiumBasisIsOtInclusive ? `${prevRateStr}/hr` : `${baseRateStr}/hr × ${currentMultiplier.defaultMultiplier!}x`})`,
      hourly_premium_rate: hourlyPremiumRateStr,
      premium_amount: `$${premiumAmountWithCalc}`,
      final_gross: `$${gross_pay.toLocaleString(undefined, { minimumFractionDigits: gross_pay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`,
      final_gross_calculation: finalGrossCalculation,
      effective_hourly_rate_calculation: effectiveHourlyRateCalculation,
    });

    hoursAccountedFor += hoursInBracket;
  }
  
  const holiday_hourly_premium_total_premium = holiday_hourly_premium_total_pay - normal_day_total_pay;

  // Total for Fixed Premium (normal + fixed premium)
  const fixed_premium_total_pay_num = Number(normal_day_total_pay) + Number(holidayFixedPremiumAmount || 100);

  // Create dynamic multiplier rows for the table
  const multiplierRows = [];
  
  // Add STRAIGHT_TIME row
  for (let i = 0; i < fullLadder.length; i++) {
    const current = fullLadder[i];
    const nextInLadder = fullLadder[i + 1];
    
    const offsetMultiplier = getOffsetTarget(i);
    
    let thresholdText;
    if (current.code === 'STRAIGHT_TIME') {
        const threshold = getThreshold(nextInLadder, billingStructure) ?? (isDayRate ? 8 : 40);
        thresholdText = `First ${threshold} hours`;
    } else {
        thresholdText = `After ${getThreshold(current, billingStructure)} hours`;
    }
    
    let description;
    
    if (current.code === 'STRAIGHT_TIME') {
      description = 'Base hours get bumped to first overtime level';
    } else if (nextInLadder) {
      description = 'Gets bumped to next level';
    } else if (offsetMultiplier.topMode === 'extended_tier') {
      description = 'Continues into extended holiday tier';
    } else {
      description = 'Capped at highest rate — enable extended tiers to go higher';
    }

    multiplierRows.push({
        name: current.name,
        threshold: thresholdText,
        holiday_offset_name: offsetMultiplier.name,
        description
    });
  }

  const highestMultiplier = fullLadder[fullLadder.length - 1];
  const highest_rate_name = highestMultiplier.name;

  return {
    example_role_rate: example_role_rate.toLocaleString(undefined, { minimumFractionDigits: example_role_rate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
    example_role_unit,
    example_role_billing_structure: billingStructure === 'day_rate' ? 'day rate' : 'hourly',
    example_shift_duration: example_shift_duration.toFixed(2).replace(/\.00$/, ''),
    normal_day_total_pay: normal_day_total_pay.toLocaleString(undefined, { minimumFractionDigits: normal_day_total_pay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
    holiday_day_total_pay: holiday_day_total_pay.toLocaleString(undefined, { minimumFractionDigits: holiday_day_total_pay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
    holiday_premium: holiday_premium.toLocaleString(undefined, { minimumFractionDigits: holiday_premium % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
    highest_rate_name,
    multiplier_rows: multiplierRows,
    normal_calculation_rows: normalCalculationRows,
    normal_calculation_formula,
    holiday_calculation_rows: holidayCalculationRows,
    // Additions for Floor Multiplier
    example_floor_multiplier_name: configuredFloorMultiplier?.name || configuredCustomFloorMultiplier?.name || exampleFloorMultiplier.name,
    example_floor_multiplier_rate: configuredFloorMultiplier
      ? `${configuredFloorMultiplier.defaultMultiplier!}x`
      : configuredCustomFloorMultiplier
        ? `${configuredCustomFloorMultiplier.defaultMultiplier!}x`
        : `${exampleFloorMultiplier.defaultMultiplier!}x`,
    example_floor_multiplier_code: configuredFloorMultiplier?.code || configuredCustomFloorMultiplier?.code || exampleFloorMultiplier.code,
    has_configured_floor_multiplier: !!configuredFloorMultiplier || configuredCustomFloorMultiplier != null,
    floor_holiday_day_total_pay: floor_holiday_day_total_pay.toLocaleString(undefined, { minimumFractionDigits: floor_holiday_day_total_pay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
    floor_holiday_premium: floor_holiday_premium.toLocaleString(undefined, { minimumFractionDigits: floor_holiday_premium % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
    // Add to the returned object near the other floor fields
    floor_holiday_calculation_rows: floorHolidayCalculationRows,
    // For the "None selected" display logic in docs/examples
    show_none_selected_for_floor: !configuredFloorMultiplier && configuredCustomFloorMultiplier == null && !hasAnyNonStraightTimeMultipliers,
    // Additions for Fixed Premium
    fixed_surcharge_amount: (holidayFixedPremiumAmount || 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    fixed_premium_total_pay: fixed_premium_total_pay_num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    // Additions for Hourly Premium
    holiday_hourly_premium_type: resolvedHolidayUpliftType,
    holiday_hourly_premium_percentage: resolvedHolidayUpliftPercentage,
    holiday_hourly_premium_amount: resolvedHolidayUpliftAmount.toLocaleString(undefined, { minimumFractionDigits: resolvedHolidayUpliftAmount % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
    holiday_hourly_premium_total_pay: holiday_hourly_premium_total_pay.toLocaleString(undefined, { minimumFractionDigits: holiday_hourly_premium_total_pay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
    holiday_hourly_premium_total_premium: holiday_hourly_premium_total_premium.toLocaleString(undefined, { minimumFractionDigits: holiday_hourly_premium_total_premium % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }),
    hourly_premium_calculation_rows: hourlyPremiumCalculationRows,
    holiday_premium_basis: holidayPremiumBasisIsOtInclusive ? 'ot_inclusive' : 'base_rate',
    getSampleRole: sampleRole,
    rateMultiplierLadder,
    proratedRateDisplay,
    Math,
  };
}
